import { type FC, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { EditOperationTypes, EditTargetTypes } from "src/constants";
import { ROUTE_EDITS } from "src/constants/route";
import type { EditFragment } from "src/graphql";
import { useDeleteEdit } from "src/graphql";

interface Props {
  edit: EditFragment;
  show: boolean;
  onHide: () => void;
}

const DeleteEditModal: FC<Props> = ({ edit, show, onHide }) => {
  const navigate = useNavigate();
  const [deleteReason, setDeleteReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deleteEdit, { loading: deleting }] = useDeleteEdit();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!deleteReason.trim()) return;

    setError(null);
    deleteEdit({
      variables: {
        input: {
          id: edit.id,
          reason: deleteReason,
        },
      },
    })
      .then(() => {
        onHide();
        navigate(ROUTE_EDITS);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to delete edit");
      });
  };

  const handleClose = () => {
    setDeleteReason("");
    setError(null);
    onHide();
  };

  const editType = `${EditOperationTypes[edit.operation]} ${EditTargetTypes[edit.target_type]}`;
  const userName = edit.user?.name || "Unknown User";
  const editIdShort = edit.id.slice(0, 8);

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          Delete {editType} - {editIdShort} by {userName}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>
              <strong>Reason for deletion (required):</strong>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter the reason for deleting this edit..."
              required
              disabled={deleting}
            />
          </Form.Group>
          {error && <div className="text-danger mt-3">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={!deleteReason.trim() || deleting}
          >
            {deleting ? "Deleting..." : "Delete Edit"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DeleteEditModal;
